/**
 * Tools store — brief §7.
 */
import { colourCountsFor, DEFAULT_COLOUR_INDEX, GRAPHITE_INDEX, isValidColourCount, paletteColourSet, type WallpaperGroupId } from '@/patterns/wallpaper';
import { isDarkMode } from '@/stores/layers';
import { usePatternStore } from '@/stores/pattern';
import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';

export type ToolId = 'pen' | 'line' | 'bezier' | 'brush-round' | 'brush-flat' | 'erase' | 'fill';

/** S/M/L line weights for the size selector. */
export type BrushSize = 'S' | 'M' | 'L';

/**
 * Sketch reads as pencil, not ink: a dark, slightly warm 3B graphite rather than
 * pure black or the user's ink colour. Layer opacity grades it toward a harder,
 * cooler 4H look (brief — sketch refinements §1).
 */
export const SKETCH_GRAPHITE = '#2a2a28';

/**
 * §4: distinct sketch/guide colours per colour-class when colour tiling is on.
 * These are construction-guide tones, NOT the artwork — muted so they read as
 * "which colour goes here", clearly recessive vs actual painted colour, but
 * distinct enough to tell classes apart at a glance. Colour class k uses index
 * k, so a class's sketch tone and its paint slot stay the "same slot" to the
 * artist even though the eventual painted colour is the artist's own choice.
 *
 * One ordered master palette per theme (count N uses the first N); light =
 * muted coloured-pencil tones, dark = softer chalky tones tuned for a dark
 * background (a muted pencil tone would go muddy on dark). Tunable defaults —
 * confirm/adjust on-device (a "muted but distinguishable" judgement call).
 */
// Light greyer (more desaturated — reads as soft pencil, recedes further);
// dark brighter (lifted so chalky tones stay legible on the dark ground).
const SKETCH_CLASS_LIGHT = ['#a67878', '#7688a4', '#83987f', '#a6967d', '#917f8e', '#6d9898', '#927d73'];
const SKETCH_CLASS_DARK = ['#e2b0b0', '#b3c3ee', '#bcdcb5', '#eddcaa', '#d6bce6', '#a9dcdc', '#dbcbb0'];

/** Muted sketch colour for a colour-class (0-based), per theme. Falls back to
 *  graphite if somehow out of range. */
export function sketchClassColour(classIndex: number, isDark: boolean): string {
  const palette = isDark ? SKETCH_CLASS_DARK : SKETCH_CLASS_LIGHT;
  return palette[classIndex] ?? SKETCH_GRAPHITE;
}

/** One tile rasterises at 512px; sizes are stored in tile units (px ÷ 512). */
const TILE_PX = 512;
/** Ink S/M/L line widths: 2 / 8 / 20 px. */
const INK_SIZES: Record<BrushSize, number> = { S: 2 / TILE_PX, M: 8 / TILE_PX, L: 20 / TILE_PX };
/** Eraser S/M/L widths (larger than ink): 4 / 22 / 52 px. */
const ERASE_SIZES: Record<BrushSize, number> = { S: 4 / TILE_PX, M: 22 / TILE_PX, L: 52 / TILE_PX };
/** Pencil (sketch) S/M/L base-radius multiplier (brief 09-layout: the merged
 *  Draw tool needs a "Brush size" control — previously the stamp had a single
 *  fixed base radius, only ever modulated by tilt, with no user-selectable
 *  size at all). Brief 11-layout2 §4: all three sizes read one step too
 *  small — shifted up (old M -> new S, old L -> new M) with a new, larger
 *  top size added; the old smallest (0.6) is retired, no longer offered. */
export const SKETCH_SIZE_MUL: Record<BrushSize, number> = { S: 1, M: 1.8, L: 3 };

/** Lowest colour-symmetry count above OFF for a group, or 1 when the group
 *  supports no colour symmetry (p1, p2, pgg, p4g). Used for the on-load
 *  default so colour symmetry starts enabled (brief 14). */
function defaultColourCount(wpgId: WallpaperGroupId): number {
  return colourCountsFor(wpgId).find((c) => c > 1) ?? 1;
}

export const useToolsStore = defineStore('tools', () => {
  const activeTool = ref<ToolId>('pen');
  /** Ink, eraser and pencil (sketch) each remember their own S/M/L size across
   *  tool switches. */
  const inkSize = ref<BrushSize>('S');
  const eraseSize = ref<BrushSize>('M');
  const sketchSize = ref<BrushSize>('M');
  const inkSizeTile = computed(() => INK_SIZES[inkSize.value]);
  const eraseSizeTile = computed(() => ERASE_SIZES[eraseSize.value]);
  const sketchSizeMul = computed(() => SKETCH_SIZE_MUL[sketchSize.value]);
  /** tessellated ghost copies: on/off only — opacity is fixed when shown */
  const showGhosts = ref(true);
  const ghostOpacity = ref(0.9);

  // --- Colour (brief 10-colours, extended by brief 11-layout2 §3) ---
  // The user picks one of 12 fixed hued pencils, or graphite, as the main
  // drawing colour; there is no custom palette, harmony editor, or arbitrary
  // hex. `baseColourIndex` is the only thing a stroke's colour choice stores.
  // Colour symmetry (off/2/3/4/6, `colourCount`) is a pure rendering
  // transform over that one index — see paletteColourSet — not separate
  // per-slot state, so there is nothing here to hand-pick or promote.
  // Defaults to graphite (DEFAULT_COLOUR_INDEX) — "first touch should be
  // pencil-on-paper, not an arbitrary hue."
  const baseColourIndex = ref(DEFAULT_COLOUR_INDEX);
  const colourCount = ref(defaultColourCount(usePatternStore().currentConfig.wpgId));
  /** Kept in step with the current theme by stores/ui.ts's theme watcher
   *  (dark/light = a different physical medium — brief's "Dark mode": same
   *  hue, different luminosity/saturation). Initialised from the DOM directly
   *  so the very first paint is correct regardless of store init order. */
  const isDark = ref(isDarkMode());

  /** `colours[0]` is the main pencil colour — kept under the historical
   *  `colours`/`color` names so every existing call site (useDrawing.
   *  brushColor, layerPlacementBitmap.ts, the PSD exporter) keeps working
   *  unchanged. Recomputes automatically on colour, symmetry, or theme
   *  change — nothing here is ever hand-edited or frozen. */
  const colours = computed(() => paletteColourSet(baseColourIndex.value, colourCount.value, isDark.value));
  const color = computed(() => colours.value[0]!);

  /** True once the artist has touched the colour system for the merged Pencil
   *  tool (brief 09-layout: "one primary drawing tool," sketch+ink UI merged).
   *  Before this, a sketch stroke renders as raw, untinted graphite exactly as
   *  before the merge; once true, it tints to `colours` the same way ink
   *  already did (services/layerPlacementBitmap.ts). Never resets back to
   *  false — once the artist has engaged with colour, staying in that mode is
   *  the simpler mental model. */
  const sketchColourActive = ref(false);

  /** Pick swatch `index` (0..GRAPHITE_INDEX, the 12 hues + graphite) as the
   *  main drawing colour. No wraparound — index is a concrete swatch, never
   *  a relative "next/previous" step. */
  function selectColour(index: number): void {
    if (index < 0 || index > GRAPHITE_INDEX) return;
    baseColourIndex.value = index;
    sketchColourActive.value = true;
  }

  /** Change how many colours are active — no-ops if `n` isn't valid for the current wallpaper group. */
  function setColourCount(n: number): void {
    const patternStore = usePatternStore();
    if (!isValidColourCount(patternStore.currentConfig.wpgId, n)) return;
    if (n > 1) sketchColourActive.value = true;
    colourCount.value = n;
  }

  // Clamp back to a valid count whenever the active wallpaper group changes
  // (switching from e.g. p6 with count=7 to p4, which doesn't support 7).
  watch(
    () => usePatternStore().currentConfig.wpgId,
    (wpgId) => {
      if (!isValidColourCount(wpgId, colourCount.value)) setColourCount(1);
    },
  );

  /**
   * How many colour-instances the current drawing needs for export (brief §4,
   * consumed by the Procreate PSD exporter — one "Paint:Style<N>" layer per
   * colour instance when this is > 1). Colour tiling in this app is a single
   * document-wide setting rather than per-region (there's no per-region colour
   * picker anywhere in the UI to hang a finer-grained count off), so every
   * exported region gets the same instance count.
   */
  const colourInstancesNeeded = computed(() => colourCount.value);

  /** The colour assigned to instance `i` (0-based) — clamps to the main colour if out of range. */
  function colourForInstance(i: number): string {
    return colours.value[i] ?? colours.value[0]!;
  }

  return {
    activeTool,
    inkSize,
    eraseSize,
    sketchSize,
    inkSizeTile,
    eraseSizeTile,
    sketchSizeMul,
    color,
    colours,
    colourCount,
    baseColourIndex,
    isDark,
    sketchColourActive,
    colourInstancesNeeded,
    colourForInstance,
    selectColour,
    setColourCount,
    showGhosts,
    ghostOpacity,
  };
});
